import { type FieldDef, type FormColumns } from '../types/dynamic-form.types';
import { type FormSectionDef } from '../types/form-section.types';

/**
 * The default ceiling.
 *
 * Four rather than two, because the ceiling is not the layout: the CONTAINER
 * decides how many of the four tracks it can afford, and a form that never gets
 * wide enough never sees more than one. Defaulting to two would put the old
 * viewport-shaped answer back in as a cap.
 */
const DEFAULT_COLUMNS: FormColumns = 4;

export interface ResolvedSection {
  id: string;
  title?: string;
  description?: string;
  fields: readonly FieldDef[];
  columns: FormColumns;
  isCollapsible: boolean;
  isInitiallyOpen: boolean;
  visibleWhen?: (values: Record<string, unknown>) => boolean;
  className?: string;
  /**
   * Renders no `<fieldset>` and no `<legend>` — either the caller passed a flat
   * `fields` list, or a section with no title. A fieldset without a legend is an
   * unnamed group: it costs a screen reader an announcement and buys nothing.
   */
  isImplicit: boolean;
}

export interface ResolveSectionsInput {
  fields?: readonly FieldDef[];
  sections?: readonly FormSectionDef[];
  columns?: FormColumns;
}

/** The layout the form actually renders: always sections, however it was authored. */
export function resolveSections({
  fields,
  sections,
  columns = DEFAULT_COLUMNS,
}: ResolveSectionsInput): readonly ResolvedSection[] {
  if (sections !== undefined) {
    return sections.map((section) => ({
      id: section.id,
      ...(section.title === undefined ? {} : { title: section.title }),
      ...(section.description === undefined ? {} : { description: section.description }),
      fields: section.fields,
      columns: section.columns ?? columns,
      isCollapsible: section.isCollapsible === true,
      isInitiallyOpen: section.isInitiallyOpen ?? true,
      ...(section.visibleWhen === undefined ? {} : { visibleWhen: section.visibleWhen }),
      ...(section.className === undefined ? {} : { className: section.className }),
      isImplicit: section.title === undefined,
    }));
  }

  return [
    {
      id: 'fields',
      fields: fields ?? [],
      columns,
      isCollapsible: false,
      isInitiallyOpen: true,
      isImplicit: true,
    },
  ];
}

/**
 * Every field the form owns, in author order, section boundaries removed.
 *
 * This is the list the value layer works from — defaults, paths, the hidden
 * strip, the server-error map. Grouping is a layout fact and must never reach
 * any of them.
 *
 * An **object group contributes itself AND its properties**, in that order, and
 * each half earns its place. The properties are what the value layer acts on:
 * `value.name` needs a default, owns the path a server 422 lands on, and is what
 * `shouldFocusError` focuses. The group is what a `visibleWhen` hides, and
 * hiding it has to strip the whole object rather than leave its properties
 * behind. The order matters to `buildDefaultValues`, which writes the group's
 * empty `{}` first and then fills it — reversed, the group would overwrite
 * leaves that were already right.
 */
export function flattenFields(sections: readonly ResolvedSection[]): readonly FieldDef[] {
  return sections.flatMap((section) =>
    // Annotated, because a property's type forbids `isTranslatable` and the
    // inferred element type would otherwise be that narrower shape rather than
    // the `FieldDef` every consumer of this list expects.
    section.fields.flatMap<FieldDef>((field) =>
      field.kind === 'object' ? [field, ...field.properties] : [field],
    ),
  );
}
